import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import '../css/CourtRulesSheet.css';
import FormattedText from './FormattedText';
import {
  COURT_RULES_TITLE,
  COURT_RULES_ACCEPT_LABEL,
  COURT_RULES_SCROLL_HINT,
  COURT_RULES_SCROLL_END_TOLERANCE_PX,
} from '../constants/bookingConst';

/**
 * Fraction (0..1) of a scroll container the reader has scrolled through.
 * Content that fits without scrolling counts as fully read.
 *
 * @param {HTMLElement} el
 * @returns {number}
 */
const getScrollProgress = (el) => {
  const scrollable = el.scrollHeight - el.clientHeight;
  if (scrollable <= COURT_RULES_SCROLL_END_TOLERANCE_PX) return 1;
  return Math.min(1, Math.max(0, el.scrollTop / scrollable));
};

/**
 * "Court Rules" popup shown between the guest's details and checkout
 * (Config -> Court Reminders -> "Show court rules before checkout").
 *
 * A card centred on every screen size, sized like the shared modal. It shows
 * the club's reminders text as typed (FormattedText), the bar at the top
 * fills as the guest scrolls, and "I Understand" unlocks only once they
 * reach the end — so the whole text has at least gone past their eyes
 * before they can agree.
 *
 * Follows the app-wide modal rules: an × close button, and no dismissal on a
 * tap outside the card.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {string} props.text - Config -> Court Reminders text
 * @param {() => void} props.onAccept - "I Understand"
 * @param {() => void} props.onClose - × (back to the details form)
 * @returns {JSX.Element|null}
 */
function CourtRulesSheet({ isOpen, text, onAccept, onClose }) {
  const bodyRef = useRef(null);
  const [progress, setProgress] = useState(0);
  // Sticky: once they have reached the end, scrolling back up to re-read
  // must not lock the button again.
  const [reachedEnd, setReachedEnd] = useState(false);

  /**
   * Updates the progress bar and the unlock state from the scroll position.
   * @returns {void}
   */
  const syncFromScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const fraction = getScrollProgress(el);
    setProgress(fraction);
    if (fraction >= 1 || el.scrollTop + el.clientHeight >= el.scrollHeight - COURT_RULES_SCROLL_END_TOLERANCE_PX) {
      setReachedEnd(true);
    }
  }, []);

  // Fresh state every time it opens, then measure once laid out — a short
  // text that needs no scrolling unlocks straight away.
  useLayoutEffect(() => {
    if (!isOpen) return;
    setReachedEnd(false);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    syncFromScroll();
  }, [isOpen, text, syncFromScroll]);

  // A rotated phone or resized window can change whether the text scrolls.
  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener('resize', syncFromScroll);
    return () => window.removeEventListener('resize', syncFromScroll);
  }, [isOpen, syncFromScroll]);

  if (!isOpen) return null;

  return (
    // No onClick on the overlay on purpose: a tap outside must not dismiss it.
    <div className="crs-overlay">
      <div className="crs-sheet" role="dialog" aria-modal="true" aria-labelledby="crs-title">
        <button type="button" className="crs-close" onClick={onClose} aria-label="Close">&times;</button>

        <div
          className="crs-progress"
          role="progressbar"
          aria-label="Rules read"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <span style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>

        <h2 id="crs-title" className="crs-title">{COURT_RULES_TITLE}</h2>

        <div className="crs-body" ref={bodyRef} onScroll={syncFromScroll}>
          <FormattedText className="crs-text" text={text} />
        </div>

        <div className="crs-foot">
          {!reachedEnd && <div className="crs-hint">{COURT_RULES_SCROLL_HINT}</div>}
          <button type="button" className="crs-accept" onClick={onAccept} disabled={!reachedEnd}>
            {COURT_RULES_ACCEPT_LABEL}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CourtRulesSheet;
